import styled from "@emotion/styled";
import { GrDatePicker, palette } from "@jnpmedi/grebob";
import { elementHeight } from "@newComponents/common/Viewer/EConsentDocsPdfViewerPageMarkerZone";
import { defaultMarkerColor, getColorBySignerIndex } from "@newComponents/common/Viewer/types";
import { useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { fadeInBorderMarker } from "@assets/styles/animations";
import { EConsentMarkerProps } from "./EConsentMarker";
import { ECONSENT_DATE_FORMAT } from "../types";
import { DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";
import { Dayjs } from "dayjs";

export function DatePickerMarker(props: EConsentMarkerProps) {
  const { markers, setMarkers } = useDocumentMarkerContext();

  const getMarkerType = (attrType: string) => {
    switch (attrType) {
      case DocumentSignerAttrType.AgentDateItemMark:
        return {
          index: DocumentSignerAttrType.AgentDateItemIndex,
          missing: DocumentSignerAttrType.AgentDateItemMissing,
          value: DocumentSignerAttrType.AgentDateItemValue,
          format: DocumentSignerAttrType.AgentDateItemFormat,
        };
      default:
        return {
          index: DocumentSignerAttrType.DateItemIndex,
          missing: DocumentSignerAttrType.DateItemMissing,
          value: DocumentSignerAttrType.DateItemValue,
          format: DocumentSignerAttrType.DateItemFormat,
        };
    }
  };

  const markerTypes = getMarkerType(props.marker.attr);

  const dateIndex = markers.find((e) => e.attr === markerTypes.index && e.attrUuid === props.marker.groupUuid);
  const dateMissing = markers.find((e) => e.attr === markerTypes.missing && dateIndex?.attrUuid === e.groupUuid);
  const dateValue = markers.find((e) => e.attr === markerTypes.value && dateIndex?.attrUuid === e.groupUuid);
  const dateFormat = markers.find((e) => e.attr === markerTypes.format && dateIndex?.attrUuid === e.groupUuid);

  const handleDateChange = (newValue: Dayjs | null) => {
    if (!newValue) {
      return;
    }

    const updatedMarkers = markers
      .filter((x) => !(x.attr === markerTypes.value && x.attrUuid === dateValue?.attrUuid && x.groupUuid === dateValue?.groupUuid))
      .concat([
        {
          id: dateValue?.id,
          signerUuid: dateValue?.signerUuid!,
          target: dateValue?.target!,
          signerIndex: dateValue?.signerIndex!,
          attr: markerTypes.value,
          attrUuid: dateValue?.attrUuid!,
          groupUuid: dateValue?.groupUuid!,
          value: newValue.format(dateFormat?.value),
          page: dateValue?.page!,
          x: dateValue?.x!,
          y: dateValue?.y!,
        },
      ]);
    setMarkers(updatedMarkers);
  };

  return (
    <StyledCalendarInputWrapper {...props} isFulfilled={!!dateValue?.value} onClick={props.onClick}>
      {props.isModeSigning ? (
        <GrDatePicker
          size="small"
          placeholder={dateMissing?.value || (dateFormat?.value === ECONSENT_DATE_FORMAT.YYYYMMDD_Slash ? "YYYY/MM/DD" : "YYYY-MM-DD")}
          format={dateFormat?.value}
          style={{ width: "140px", border: "none", height: "20px", padding: "0 3px 0px 1px" }}
          onChange={handleDateChange}
        />
      ) : (
        props.children
      )}
    </StyledCalendarInputWrapper>
  );
}

const StyledCalendarInputWrapper = styled("div")<EConsentMarkerProps & { isFulfilled?: boolean }>((props) => ({
  display: props.isHidden ? "none" : "block",
  userSelect: "none",
  boxSizing: "border-box",
  position: "absolute",
  height: elementHeight,
  top: props.top,
  left: props.left,
  transform: "translateY(-50%)",
  borderRadius: "2px",
  border: props.isModeSigning
    ? `2px solid ${palette[defaultMarkerColor.additional.border]}`
    : `2px solid ${palette[getColorBySignerIndex(props.marker.signerIndex).border]}`,

  outline: "none",

  backgroundColor: props.isModeSigning
    ? defaultMarkerColor.additional.background
    : getColorBySignerIndex(props.marker.signerIndex).background,

  color: "#878d96",
  fontSize: "10px",
  fontWeight: 500,
  lineHeight: "135%",
  whiteSpace: "nowrap",
  textIndent: "0",

  cursor: "pointer",

  "&:hover": {
    backgroundColor: props.isModeSigning
      ? defaultMarkerColor.additional.backgroundHover
      : getColorBySignerIndex(props.marker.signerIndex).backgroundHover,

    //NOTE: marker hover 시, UnlinkButton이 보임
    "& > span": {
      opacity: 1,
    },
  },

  "&":
    !props.isFulfilled && props.isModeSigning
      ? {
          animation: `${fadeInBorderMarker} 1.6s ease-in-out infinite`,
        }
      : "",
}));
